import { useCallback, useEffect, useState } from 'react';
import type { YouTubeConnectionStatus } from '@liveforge/shared';
import { errorMessage } from '../lib/api';
import { connectYouTube, disconnectYouTube, getYouTubeStatus } from '../lib/resources';

export interface YouTubeConnectionResult {
  status: YouTubeConnectionStatus | null;
  loading: boolean;
  error: string | null;
  /** True while the browser is being handed over to Google, or coming back. */
  busy: boolean;
  /** Leaves the app for the consent screen. Does not resolve on success. */
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
  reload: () => void;
}

/**
 * The YouTube binding of one Show.
 *
 * Kept out of `useShow` on purpose: the status costs a round trip the Show
 * page does not otherwise need, and the connection is reloaded on its own
 * after the OAuth round trip lands back on this page.
 */
export function useYouTubeConnection(showId: string | undefined): YouTubeConnectionResult {
  const [status, setStatus] = useState<YouTubeConnectionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!showId) {
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    getYouTubeStatus(showId, controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return;
        setStatus(data);
        setError(null);
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setError(errorMessage(cause));
      })
      .finally(() => {
        if (controller.signal.aborted) return;
        setLoading(false);
      });
    return () => controller.abort();
  }, [showId, reloadToken]);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  const connect = useCallback(async () => {
    if (!showId) return;
    setError(null);
    setBusy(true);
    try {
      const { authUrl } = await connectYouTube(showId);
      // `assign`, not `replace`: the browser's Back button should return the
      // user to the Show if they change their mind on Google's screen.
      window.location.assign(authUrl);
      // Deliberately left busy. The page is on its way out, and clearing the
      // flag would flash the button back to life during the navigation.
    } catch (cause) {
      setError(errorMessage(cause));
      setBusy(false);
    }
  }, [showId]);

  const disconnect = useCallback(async () => {
    if (!showId) return;
    setError(null);
    setBusy(true);
    try {
      await disconnectYouTube(showId);
      setStatus((current) => (current ? { ...current, connection: null } : current));
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }, [showId]);

  return { status, loading, error, busy, connect, disconnect, reload };
}
